// DynamicTable.ts
import styled, { keyframes } from "styled-components";
import { ate, ALVO_DE_TOQUE } from "../global/breakpoints";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/*
 * A CAIXA DA TABELA.
 *
 * Tinha `height: 100vh` no modo normal. Uma tabela que declara ter a altura da
 * JANELA dentro de uma página que já rola empurra tudo o que vem depois dela
 * para fora da primeira dobra, e some com a paginação em tela baixa. Agora ela
 * ocupa o que precisa; quem limita a altura é o container de rolagem.
 */
export const DynamicTableContainer = styled.div<{ isExpanded?: boolean }>`
  width: 100%;
  min-width: 0;
  ${(props) =>
    props.isExpanded &&
    `
      position: fixed;
      top: 0;
      left: 0;
      z-index: 9999;
      height: 100vh;
      width: 100vw;
      overflow: hidden;
      padding: 0 0px 0 0px;
      background-color: ${props.theme.colors.background};
  `}
`;

/*
 * A LARGURA DA TABELA — de conta em vw para 100% do que existe.
 *
 * O que havia: `max-width` escolhido por um if sobre as duas barras — 45vw com
 * as duas abertas, 60vw com uma, 75vw com nenhuma. Três problemas de uma vez:
 *
 *  1. `vw` é a JANELA INTEIRA, incluindo o pedaço que as barras ocupam. Com as
 *     duas abertas em 1440 sobram ~700 de área útil, mas 45vw dá 648 — e a
 *     margem do conteúdo ainda tira mais. A tabela ficava menor que o espaço
 *     que tinha, e a rolagem horizontal aparecia cedo demais.
 *  2. Em 1920 a MESMA regra dá 864, enquanto a área útil é ~1220: sobrava um
 *     terço da tela vazio ao lado da tabela.
 *  3. As barras podem estar abertas e NÃO ocupar espaço (abaixo de 1440 elas
 *     viram gaveta e flutuam por cima) — aí a conta erra por 700.
 *
 * Agora: 100% do container. Quem sabe a largura disponível é o layout, não uma
 * regra de três sobre a janela. As props continuam na assinatura porque os oito
 * pontos que usam este componente já as passam; elas deixaram de decidir
 * largura.
 */
export const DynamicTableWrapper = styled.div<{
  isLeftSidebarVisible?: boolean;
  isRightSidebarVisible?: boolean;
  isExpanded?: boolean;
}>`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  ${(props) =>
    props.isExpanded &&
    `
      height: 90vh;
  `}
`;

/*
 * DENSIDADE E ZOOM — as duas classes que o FlyOps usa, traduzidas para cá.
 *
 * Lá (`SPEC_TABLE_DENSITY.md`) o compacto derruba o padding da célula de 1rem
 * para 0,3rem e a altura do cabeçalho de 3rem para 2,1rem, e o zoom entra por
 * uma variável CSS (`--tbl-font-size`) aplicada no embrulho de rolagem. É o
 * mesmo desenho aqui, com duas diferenças que são do Central:
 *
 *  · os valores saem da escala fluida (`--u`), então o compacto de 1440 é o
 *    mesmo compacto proporcional em 1920 e em 1366;
 *  · a variável é `--fs-tabela`, e as células a leem sempre — no confortável
 *    ela vale `var(--fs-sm)`, então não há caminho especial para o zoom 1.
 */
export const DynamicTableScrollContainer = styled.div<{
  isExpanded?: boolean;
  $compacta?: boolean;
  $escala?: number;
}>`
  --fs-tabela: calc(var(--fs-sm) * ${(props) => props.$escala ?? 1});

  ${(props) =>
    props.$compacta &&
    `
    td {
      padding-top: calc(var(--u) * 4);
      padding-bottom: calc(var(--u) * 4);
      padding-left: calc(var(--u) * 7);
      padding-right: calc(var(--u) * 7);
    }
    th {
      padding-top: calc(var(--u) * 2);
      padding-bottom: calc(var(--u) * 2);
      padding-left: calc(var(--u) * 7);
      padding-right: calc(var(--u) * 7);
    }
  `}

  flex: 1;
  /* Piso em 240px: com 54vh puro, uma janela de 600 de altura dava 324 menos o
     cabeçalho e o filtro — sobrava UMA linha de dado. */
  max-height: ${(props) =>
    props.isExpanded ? "78.2vh" : "clamp(240px, 54vh, 70vh)"};
  overflow: auto;
  /* A tabela é o único lugar do app onde rolar de lado é CERTO: dez colunas não
     cabem num telefone e encolher a coluna até virar reticências é pior que
     arrastar. O que não pode é a PÁGINA rolar de lado — por isso o overflow
     mora aqui dentro. */
  overscroll-behavior-x: contain;
  background-color: ${(props) => props.theme.colors.background};
  transition: all 0.3s ease;

  &::-webkit-scrollbar {
    width: calc(var(--u) * 15);
    height: calc(var(--u) * 15);
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.selection};
    border-radius: 10px;
    border: 2px solid ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${(props) => props.theme.colors.selectionDark};
    cursor: grab;
  }

  &::-webkit-scrollbar-thumb:active {
    cursor: grabbing;
  }
  &:hover {
    scrollbar-width: auto;
  }
  scrollbar-color: ${(props) => props.theme.colors.selection};
`;

export const HeaderWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--sp-12);
  border-top-right-radius: 10px;
  border-top-left-radius: 10px;
  padding: calc(var(--u) * 30);

  ${ate.tablet} {
    padding: var(--sp-12);
  }
  background-color: ${(props) => props.theme.colors.modalColor};
  width: 100%;
  box-sizing: border-box;
`;

export const TableTitle = styled.h3`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  text-align: left;
  font-size: var(--fs-2xl);
  overflow-wrap: anywhere;
  color: ${(props) => props.theme.colors.selection};
  text-shadow: -0.5px -0.5px 0.5px black, 0.5px -0.5px 0.5px black,
    -0.5px 0.5px 0.5px black, 0.5px 0.5px 0.5px black;
`;

export const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--sp-10);
  position: relative;
`;

export const Button = styled.button<{
  primary?: boolean;
  edit?: boolean;
  pagination?: boolean;
  active?: boolean;
  importButton?: boolean;
  shadow?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-8);
  border-radius: 8px;
  border: none;
  /*
   * AS CORES DE ACAO, DEPOIS DA CONTA.
   *
   * O que estava aqui era um verde e um azul claros com o texto em branco
   * TRANSLUCIDO ("#ffffffba" = branco a 73%). As duas coisas somadas apagavam o
   * rotulo. Medido no navegador, com a transparencia composta de verdade:
   *
   *     Criar SKU   branco 73% sobre #50e357     1,44:1
   *     editar      branco 73% sobre #007bff90   1,92:1
   *
   * O minimo e' 4,5:1. Nao era detalhe: "Criar SKU" e' a acao principal da
   * tela, e o lapis e' o unico jeito de abrir uma linha.
   *
   * Duas mudancas, e a primeira e' a que resolve: o branco volta a ser OPACO.
   * Branco a 73% sobre um fundo claro nao e' branco, e' o fundo — a "elegancia"
   * da transparencia estava comendo o proprio texto. Depois disso os fundos
   * foram escurecidos ate' o branco passar, mantendo matiz e saturacao (a mesma
   * derivacao que o sistema de design usa): o verde de #50E357 para #16891B
   * (4,54:1) e o azul de #007BFF para #0072ED (4,54:1). Continuam sendo o verde
   * de criar e o azul de editar; sao os mesmos tons, na luminosidade que o
   * texto aguenta.
   */
  background-color: ${(props) => {
    if (props.primary) return "#16891B";
    if (props.edit) return "#0072ED";
    if (props.pagination) return props.active ? "#ccc" : "transparent";
    return props.theme.colors.selection;
  }};
  color: ${(props) => {
    if (props.primary || props.edit) return "#ffffff";
    if (props.pagination) return props.theme.colors.selection;
    return props.theme.colors.textOnPrimary;
  }};
  font-weight: bold;
  font-size: var(--fs-md);
  cursor: pointer;
  transition: all 0.3s ease;
  /* Alvo de toque: os botões do cabeçalho (criar, importar, exportar, colunas)
     tinham só 8px de padding e viravam alvos de ~20px no monitor da loja. */
  min-height: ${ALVO_DE_TOQUE};
  gap: var(--sp-6);
  white-space: nowrap;

  &:hover {
    transform: scale(1.05);
    background-color: ${(props) => {
      /* Mais escuros que o repouso, senao o hover PERDE contraste em vez de
         ganhar — que era o caso do "#3cbf47" contra o verde novo. */
      if (props.primary) return "#0F6B13";
      if (props.edit) return "#005BC4";
      if (props.pagination) return props.theme.colors.selection + "40";
      return props.theme.colors.selectionDark;
    }};
    color: ${(props) => {
      if (props.primary || props.edit) return "white";
      return props.theme.colors.background;
    }};
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  ${(props) =>
    props.importButton &&
    `
    background-color: #ffa500;
    color: #000;

    &:hover {
      background-color: #ff8c00;
      color: #000;
    }
  `}
  ${(props) =>
    props.shadow &&
    `
 text-shadow: -0.5px -0.5px 0.5px black, 0.5px -0.5px 0.5px black,
      -0.5px 0.5px 0.5px black, 0.5px 0.5px 0.5px black;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.3);
  `}
`;
/**
 * Os dois controles de densidade, no molde do FlyOps: um alterna
 * Compacto/Confortável, o outro cicla o zoom da fonte e MOSTRA o valor atual
 * ("A 110%") — sem o número, ninguém sabe em que passo está.
 */
export const BotaoDeDensidade = styled(Button)<{ $ativo?: boolean }>`
  /*
   * Botão SECUNDÁRIO, e por isso é contornado em vez de preenchido.
   *
   * "Criar SKU" e "Importar" mudam DADO; estes dois mudam só a forma de olhar.
   * Pintá-los com o mesmo peso do botão principal disputaria a atenção com a
   * ação que importa.
   *
   * A SUPERFICIE DESLIGADA E' A DO TEMA, e nao branco fixo — que era o que
   * estava aqui.
   *
   * O branco entrou como remendo, na epoca em que o "colors.background" do tema
   * escuro era um cinza-oliva (rgb 138,136,121) sobre o qual o azul da selecao
   * dava 1,02:1. O proprio comentario daquele remendo dizia que ele valia
   * "enquanto a paleta nova do icone nao entra". Ela entrou — e o remendo virou
   * o defeito: no tema escuro "selection" e o ladrilho AMARELO, e amarelo sobre
   * branco fixo da 1,22:1. Medido no navegador: 4 elementos, os dois controles
   * de densidade e seus icones.
   *
   * Com a superficie do tema, os dois casos fecham: no claro e' tinta sobre o
   * creme (5,2:1), no escuro e' ladrilho sobre a tela #252525 (bem acima do
   * minimo). Nenhum dos dois precisa de excecao.
   */
  background-color: ${(props) =>
    props.$ativo ? props.theme.colors.selection : props.theme.colors.background};
  /* Ligado, o chip e' preenchido com a cor de acao: quem vai por cima e' o
     token que cada tema elegeu para isso (branco no claro, carvao no escuro). */
  color: ${(props) =>
    props.$ativo ? props.theme.colors.textOnPrimary : props.theme.colors.selection};
  border: 1px solid ${(props) => props.theme.colors.selection};
  font-size: var(--fs-xs);
  font-weight: 600;
  padding-inline: var(--sp-10);
  text-shadow: none;

  &:hover:not(:disabled) {
    background-color: ${(props) => props.theme.colors.selection};
    color: ${(props) => props.theme.colors.textOnPrimary};
  }

  .material-icons,
  div {
    font-size: var(--fs-base);
  }
`;

export const ExportButton = styled(Button)`
  background-color: #ffa500;
  color: #000;

  &:hover {
    background-color: #ff8c00;
    color: #000;
  }
`;

export const ExportMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${(props) => props.theme.colors.modalColor};
  border: 1px solid ${(props) => props.theme.colors.text};
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  padding: var(--sp-10);
  z-index: 10;
  animation: ${fadeIn} 0.3s ease-in-out;
  margin-top: var(--sp-5);
  min-width: calc(var(--u) * 200);
`;

export const ExportMenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: var(--sp-8) var(--sp-12);
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${(props) => props.theme.colors.selection + "40"};
  }
`;

export const ExpandButton = styled.button`
  position: absolute;
  top: 0px;
  right: calc(var(--u) * -10);
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${(props) => props.theme.colors.selection};
  text-shadow: -0.5px -0.5px 0.5px black, 0.5px -0.5px 0.5px black,
    -0.5px 0.5px 0.5px black, 0.5px 0.5px 0.5px black;
  z-index: 10;
`;

export const Table = styled.table`
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
`;

export const TableHeader = styled.thead`
  background-color: ${(props) => props.theme.colors.modalColor};
  position: sticky;
  top: 0;
  z-index: 3;
`;

export const TableBody = styled.tbody`
  background-color: ${(props) => props.theme.colors.primary};
`;

export const TableHeaderRow = styled.tr``;

export const TableRow = styled.tr<{ isSelected?: boolean }>`
  border-bottom: 1px solid ${(props) => props.theme.colors.text};
  cursor: pointer;
  transition: background-color 0.2s;

  ${(props) =>
    props.isSelected &&
    `
      background: ${props.theme.colors.modalColor + "90"};
  `}

  &:hover {
    background-color: ${(props) => props.theme.colors.modalColor + "40"};
  }
`;

interface StickyProps {
  fixed?: boolean;
  left?: string;
  right?: string;
}

export const TableHeaderCell = styled.th<StickyProps>`
  color: ${(props) => props.theme.colors.selection};
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: var(--sp-8);
  box-sizing: border-box;
  /* Era fs-lg (18): o cabeçalho ficava MAIOR que o dado da linha e comia a
     largura útil de cada coluna. Agora acompanha o zoom junto com as células. */
  font-size: var(--fs-tabela, var(--fs-sm));
  line-height: 1.25;
  text-shadow: -0.5px -0.5px 0.5px black, 0.5px -0.5px 0.5px black,
    -0.5px 0.5px 0.5px black, 0.5px 0.5px 0.5px black;
  position: ${({ fixed }) => (fixed ? "sticky" : "static")};
  left: ${({ left }) => left || "auto"};
  right: ${({ right }) => right || "auto"};

  ${({ fixed, theme, left, right }) =>
    fixed
      ? `
        z-index: 2;
        background-color: ${theme.colors.modalColor}90;
        ${left ? `left: ${left}; box-shadow: 2px 0 5px rgba(0,0,0,0.1);` : ""}
        ${
          right
            ? `right: ${right}; box-shadow: -2px 0 5px rgba(0,0,0,0.1);`
            : ""
        }
      `
      : ""}
`;

export const TableCell = styled.td<StickyProps>`
  padding: var(--sp-10);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Cai para --fs-sm quando ninguém mexeu no zoom. */
  font-size: var(--fs-tabela, var(--fs-sm));
  line-height: 1.25;
  position: ${({ fixed }) => (fixed ? "sticky" : "static")};
  left: ${({ left }) => left || "auto"};
  right: ${({ right }) => right || "auto"};
  box-sizing: border-box;

  ${({ fixed, theme, left, right }) =>
    fixed
      ? `
        z-index: 2;
        background-color: ${theme.colors.modalColor}90;
        ${left ? `left: ${left}; box-shadow: 2px 0 5px rgba(0,0,0,0.1);` : ""}
        ${
          right
            ? `right: ${right}; box-shadow: -2px 0 5px rgba(0,0,0,0.1);`
            : ""
        }
      `
      : ""}
`;

export const Resizer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: calc(var(--u) * 5);
  height: 100%;
  cursor: col-resize;
  user-select: none;
  background-color: transparent;

  &:hover {
    background-color: ${(props) => props.theme.colors.text};
  }
`;

export const InputFilter = styled.input`
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: var(--sp-6) var(--sp-8);
  border: 1px solid;
  border-radius: 8px;
  background: ${(props) => props.theme.colors.primary};

  &::placeholder {
    color: ${(props) => props.theme.colors.text};
  }
`;

export const DropdownFilter = styled.div`
  position: relative;
  width: 100%;

  select {
    width: 100%;
    box-sizing: border-box;
    padding: var(--sp-6) var(--sp-8);
    border-radius: 8px;
    outline: none;
    background: ${(props) => props.theme.colors.primary};
    cursor: pointer;
  }
`;

export const ColumnSelectorWrapper = styled.div`
  position: absolute;
  top: calc(var(--u) * 40);
  right: 0;
  background: ${(props) => props.theme.colors.modalColor};
  border: 1px solid ${(props) => props.theme.colors.text};
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  padding: var(--sp-10);
  z-index: 10;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

export const ColumnSelectorItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: var(--sp-8);

  &:last-child {
    margin-bottom: 0;
  }
`;

export const Checkbox = styled.input`
  margin-right: var(--sp-8);
`;

export const PaginationContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-8);
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => props.theme.colors.modalColor};
  padding: var(--sp-10);
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;
  font-size: var(--fs-md);
  font-weight: bold;
`;

export const ItemsPerPageContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const SelectItemsPerPage = styled.select`
  margin: var(--sp-5);
  padding: var(--sp-5);
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.border};
  background-color: ${(props) => props.theme.colors.background};

  &:focus {
    border-color: black;
    outline: none;
  }
`;

export const PageButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-2);
`;

export const PaginationButton = styled.button<{
  active?: boolean;
  disabled?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  font-size: var(--fs-xl);

  background-color: ${(props) => {
    if (props.active) return props.theme.colors.selection;
    if (props.disabled) return props.theme.colors.selection + "20";
    return "transparent";
  }};
  /*
   * O CINZA DO BOTAO INATIVO NAO PODE SER A PROPRIA COR DELE COM ALPHA.
   *
   * Era "selection + 60" (a tinta a 37%) sobre "selection + 20" (a mesma tinta
   * a 12%). Duas camadas do MESMO tom sobre o mesmo fundo tendem ao mesmo
   * cinza: medido no navegador, com a transparencia composta, o "chevron_right"
   * e o "last_page" davam 1,64:1 — a seta praticamente nao existe.
   *
   * E' pior que um desabilitado comum, que a norma isenta: aqui o DOM diz que o
   * botao esta HABILITADO (disabled=false, cursor pointer), entao ele precisa
   * ser lido. O carvao-2 e' o cinza de apoio do sistema de design — continua
   * dizendo "este esta apagado" e chega a 5:1 sobre o fundo composto.
   */
  color: ${(props) => {
    if (props.active) return props.theme.colors.textOnPrimary;
    if (props.disabled) return props.theme.colors.label;
    return props.theme.colors.selection;
  }};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.3s ease;
  text-shadow: ${(props) =>
    !props.active
      ? "-0.5px -0.5px 0.5px black, 0.5px -0.5px 0.5px black, -0.5px 0.5px 0.5px black, 0.5px 0.5px 0.5px black"
      : "none"};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;

  &:hover:not(:disabled) {
    background-color: ${(props) => {
      if (props.active) return props.theme.colors.selection;
      return props.theme.colors.selection + "40";
    }};
    transform: ${(props) => (!props.disabled ? "translateY(-2px)" : "none")};
    box-shadow: ${(props) =>
      !props.disabled ? "0 4px 8px rgba(0, 0, 0, 0.3)" : "none"};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: width 0.3s, height 0.3s;
  }

  &:active:not(:disabled)::after {
    width: 100%;
    height: 100%;
    border-radius: 8px;
  }
`;

export const CurrentPageInfo = styled.div`
  display: flex;
  align-items: center;
  padding: var(--sp-10);
  background-color: ${(props) => props.theme.colors.selection + "20"};
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.colors.selection + "30"};
`;

export const PaginationMaterialIcons = styled.div`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-3xl);
`;

export const MaterialIcons = styled.div`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-3xl);
  margin-right: var(--sp-8);
`;

export const MaterialIconsButton = styled.div`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  padding: var(--sp-5);
  cursor: pointer;
  transition: color 0.2s;
`;

export const CellContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-5);
`;
